import { useHistory } from 'react-router-dom';
import UAParser from 'ua-parser-js';

// [Rebranding] This will need to be updated with domain updates
const OTTO_LOGO_URL = 'http://flow.televet.com/favicon/favicon-32x32.png';

export enum BrowserNotificationPermissions {
  Denied = 'denied',
  Granted = 'granted',
  Default = 'default',
}

export interface ICreateNotification {
  title?: string;
  body: string;
  clickRedirectPath?: string;
}

interface IUseBrowserNotifications {
  createNotification: (options: ICreateNotification) => void;
}

const useBrowserNotifications = (): IUseBrowserNotifications => {
  const history = useHistory();

  const createNotification = ({ title, body, clickRedirectPath }: ICreateNotification): void => {
    const notificationTitle = title || 'New Otto notification';
    /**
     * This is the info that accompanies a notification. The `channelId` being passed is the channel we
     * want to navigate to if we have it. The default behavior is to just open the clickRedirectPath.
     */
    const options: NotificationOptions = {
      icon: OTTO_LOGO_URL,
      badge: OTTO_LOGO_URL,
      body,
      data: {
        clickRedirectPath,
      },

      /**
       * Below are the button options the user has to choose from in the notification itself. The default behavior
       * when clicking anywhere in the notification is to go to the conversation. The only action that will not go to the
       * conversation is to explicitly click the close button.
       */
      actions: [
        { action: 'view', title: 'View' },
        { action: 'close', title: 'Close' },
      ],
    };

    if (!('Notification' in window)) {
      console.warn('This browser does not support desktop notifications.');
    } else if (Notification.permission === BrowserNotificationPermissions.Granted) {
      /**
       * We only want to try to send a notification if the permissions are already explicitly granted. We won't request
       * permissions here, because the user won't have any context for where the notifications are coming from. We will explicitly
       * request those when they toggle the notification settings option in the "My Notifications" settings page
       *  */

      try {
        const { getBrowser } = new UAParser();

        /**
         * Safari doesn't seem to work well with service workers showing notifications
         */
        if (getBrowser()?.name?.toLowerCase() === 'safari') {
          const notification = new Notification(notificationTitle, options);
          notification.onclick = (): void => history.push(clickRedirectPath || '/');
        } else {
          navigator.serviceWorker.getRegistration().then((reg) => {
            if (reg && reg.showNotification) reg.showNotification(notificationTitle, options);
          });
        }
      } catch (e) {
        console.error(e);
      }
    }
  };

  return { createNotification };
};

export default useBrowserNotifications;
